import { useState, useEffect, useRef, useCallback } from "react";
import API from "../config/api.js";

const API_BASE = "https://platform.socialjet.ai/app/api-assistant-proxy.php"; // proxy local
const MAX_IMAGES = 4;

// ─────────────────────────────────────────────
// UTILITIES
// ─────────────────────────────────────────────

function assistantFormatTimestamp(timestamp) {
  return new Intl.DateTimeFormat("en-US", {
    month: "short", day: "numeric", hour: "numeric", minute: "2-digit", hour12: true,
  }).format(new Date(timestamp)).replace(" ", ", ");
}

function assistantGetUserDateTime() {
  const now = new Date();
  return JSON.stringify({
    datetime: now.toLocaleString(),
    iso: now.toISOString(),
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    weekday: now.toLocaleDateString(undefined, { weekday: "long" }),
    day: now.getDate().toString().padStart(2, "0"),
    month: now.toLocaleDateString(undefined, { month: "long" }),
    year: now.getFullYear().toString(),
    time: now.toTimeString().split(" ")[0],
  });
}

function assistantEscapeHtml(text) {
  return String(text)
    .replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;").replace(/'/g, "&#039;");
}

function assistantIsImageUrl(url) { return /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i.test(url); }
function assistantIsVideoUrl(url) { return /\.(mp4|webm|ogg|mov)(\?.*)?$/i.test(url); }

function assistantParseMarkdown(text) {
  if (!text) return "";
  if (typeof window !== "undefined" && window.marked) {
    const renderer = new window.marked.Renderer();
    renderer.image = function ({ href, title, text }) {
      const src = assistantEscapeHtml(href || "");
      const alt = assistantEscapeHtml(text || "");
      const safeTitle = title ? ` title="${assistantEscapeHtml(title)}"` : "";
      return `<img src="${src}" alt="${alt}" ${safeTitle} class="img-fluid rounded assistant-markdown-image" loading="lazy" />`;
    };
    renderer.link = function ({ href, title, text }) {
      const url = href || "";
      const safeUrl = assistantEscapeHtml(url);
      const safeText = text || safeUrl;
      const safeTitle = title ? ` title="${assistantEscapeHtml(title)}"` : "";
      if (assistantIsImageUrl(url)) return `<img src="${safeUrl}" alt="${assistantEscapeHtml(safeText)}" ${safeTitle} class="img-fluid rounded assistant-markdown-image" loading="lazy" />`;
      if (assistantIsVideoUrl(url)) return `<video controls playsinline class="img-fluid rounded assistant-markdown-video" ${safeTitle}><source src="${safeUrl}" />Your browser does not support video.</video>`;
      return `<a href="${safeUrl}" target="_blank" rel="noopener noreferrer" ${safeTitle}>${safeText}</a>`;
    };
    return window.marked.parse(text, { gfm: true, breaks: true, renderer });
  }
  return assistantEscapeHtml(text)
    .replace(/!\[([^\]]*)\]\((https?:\/\/[^\s)]+)\)/gi, (_, alt, src) => {
      if (!assistantIsImageUrl(src)) return _;
      return `<img src="${assistantEscapeHtml(src)}" alt="${assistantEscapeHtml(alt)}" class="img-fluid rounded assistant-markdown-image" loading="lazy" />`;
    })
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/gi, (_, label, url) => {
      if (assistantIsImageUrl(url)) return `<img src="${assistantEscapeHtml(url)}" alt="${assistantEscapeHtml(label)}" class="img-fluid rounded assistant-markdown-image" loading="lazy" />`;
      if (assistantIsVideoUrl(url)) return `<video controls playsinline class="img-fluid rounded assistant-markdown-video"><source src="${assistantEscapeHtml(url)}" />Seu navegador não suporta vídeo.</video>`;
      return `<a href="${assistantEscapeHtml(url)}" target="_blank" rel="noopener noreferrer">${assistantEscapeHtml(label)}</a>`;
    })
    .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>")
    .replace(/\*(.*?)\*/g, "<em>$1</em>")
    .replace(/\n/g, "<br>");
}

// Converte "/threads/ABC/messages?user_id=123"
// em      "/api-assistant-proxy.php?p=/threads/ABC/messages&user_id=123"
async function assistantApiFetch(path, options = {}) {
  const [apiPath, apiQuery] = path.split("?");
  const params = new URLSearchParams(apiQuery || "");
  params.set("p", apiPath); // "p" vai na frente
  // Reordena para que "p" seja o primeiro parâmetro (mais legível nos logs)
  const finalParams = new URLSearchParams();
  finalParams.set("p", apiPath);
  params.forEach((v, k) => { if (k !== "p") finalParams.set(k, v); });

  const url = `${API_BASE}?${finalParams.toString()}`;
  const res = await fetch(url, {
    ...options,
    credentials: "include", // send the session cookie on cross-origin requests
    headers: { "Content-Type": "application/json", ...(options.headers || {}) },
  });
  if (!res.ok) throw new Error(`API error ${res.status}: ${path}`);
  return res.json();
}

function assistantExtractRequiredAction(result) {
  if (result.required_action) return result.required_action;
  if (result.requires_action) return result.requires_action;
  if (result.response && typeof result.response === "string") {
    try {
      const parsed = JSON.parse(result.response);
      if (parsed.required_action) return parsed.required_action;
      if (parsed.requires_action) return parsed.requires_action;
    } catch (_) {}
  }
  return null;
}

// ─────────────────────────────────────────────
// COOKIE HELPERS
// ─────────────────────────────────────────────
function assistantSetCookie(name, value, days) {
  const expires = new Date(Date.now() + days * 864e5).toUTCString();
  document.cookie = `${name}=${encodeURIComponent(value)};expires=${expires};path=/`;
}
function assistantGetCookie(name) {
  return decodeURIComponent(document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`))?.[1] ?? "");
}
function assistantDeleteCookie(name) {
  document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/`;
}

// ─────────────────────────────────────────────
// SUB-COMPONENTS
// ─────────────────────────────────────────────

function AssistantMessage({ message }) {
  const { role, content, id, timestamp } = message;
  const html = assistantParseMarkdown(content);
  if (role === "system" || role === "user_system") return null;
  if (role === "assistant") {
    return (
      <div id={id} className="assistant_message assistant_message--assistant d-flex align-items-start animate__animated animate__fadeIn">
        <img src="assets/img/icon-assistant.png" className="assistant_avatar rounded-circle me-2" alt="Estelle" />
        <div className="assistant_bubble assistant_bubble--assistant shadow-sm border" dangerouslySetInnerHTML={{ __html: html }} />
      </div>
    );
  }
  return (
    <>
      <div id={id} className="assistant_message assistant_message--user d-flex justify-content-end animate__animated animate__fadeIn">
        <div className="assistant_bubble assistant_bubble--user shadow-sm" dangerouslySetInnerHTML={{ __html: html }} />
      </div>
      {timestamp && <div className="assistant_timestamp text-end text-muted small">{assistantFormatTimestamp(timestamp)}</div>}
    </>
  );
}

function AssistantThreadList({ threads, onLoad, onDelete, onChangeTitle, onNewChat }) {
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  function assistantStartEditTitle(thread) { setEditingId(thread.id); setEditTitle(thread.title); }
  function assistantSaveTitle(id) { onChangeTitle(id, editTitle); setEditingId(null); }
  return (
    <div className="assistant_threads">
      <div className="p-3">
        <button className="btn btn-secondary text-start w-100" onClick={onNewChat}>New Chat <i className="fi fi-rr-plus float-end" /></button>
      </div>
      <div id="assistant_thread-list">
        {threads.map((thread) => {
          const date = new Date(thread.created_at).toLocaleString("en-US", { month: "long", day: "numeric", hour: "numeric", minute: "2-digit" });
          return (
            <div key={thread.id} className="assistant_thread-item border-top p-2 py-3">
              {editingId === thread.id ? (
                <div className="input-group input-group-sm">
                  <input type="text" className="form-control" value={editTitle} onChange={(e) => setEditTitle(e.target.value)} autoComplete="off" />
                  <button type="button" className="btn btn-outline-primary" onClick={() => assistantSaveTitle(thread.id)}>Save</button>
                </div>
              ) : (
                <div className="assistant_thread-row position-relative">
                  <button className="assistant_thread-title btn text-decoration-none fw-bold text-start text-truncate p-0 w-100" onClick={() => onLoad(thread.id)}>{thread.title}</button>
                  <span className="assistant_thread-actions position-absolute top-50 end-0 translate-middle-y">
                    <button className="btn btn-sm p-0" onClick={() => assistantStartEditTitle(thread)} title="Rename"><i className="fi fi-rr-pencil" /></button>
                    <button className="btn btn-sm btn-link p-0 ms-2 text-danger" onClick={() => onDelete(thread.id)} title="Delete"><i className="fi fi-rr-trash" /></button>
                  </span>
                </div>
              )}
              <div className="assistant_thread-date fw-normal px-3 small text-muted">{date}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function AssistantImagePreview({ images, onRemove }) {
  if (!images.length) return null;
  return (
    <div className="assistant_image-preview row g-2 mb-2">
      {images.map((img) => (
        <div key={img.idimage} className="col-3 position-relative">
          <img src={`${API.baseUrl}/upload/image/${img.idimage}/image/350x350.jpg`} onError={(e) => (e.target.src = "assets/img/350x350.png")} className="img-fluid rounded border" alt="" />
          <button type="button" className="assistant_image-remove btn btn-transparent p-0 position-absolute top-0 end-0" onClick={() => onRemove(img.idimage)}>
            <i className="fi fi-rr-cross-small" />
          </button>
        </div>
      ))}
    </div>
  );
}

function AssistantTypingIndicator() {
  return (
    <div className="assistant_message assistant_message--assistant d-flex align-items-start">
      <img src="assets/img/icon-assistant.png" className="assistant_avatar rounded-circle me-2" alt="Estelle" />
      <div className="assistant_bubble assistant_bubble--assistant shadow-sm border assistant_bubble--typing">
        <span className="assistant_typing-dot" /><span className="assistant_typing-dot" /><span className="assistant_typing-dot" />
      </div>
    </div>
  );
}

function AssistantLoadingSpinner() {
  return (
    <div className="assistant_loading rounded position-relative h-100 w-100">
      <div className="assistant_loading-spinner" />
      <div className="position-absolute translate-middle top-50 start-50 text-center">
        <img src="assets/img/icon-assistant.png" className="rounded-circle assistant_loading-icon" alt="Loading" />
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────

export default function AssistantWidget({ userId, userName = "User", userNiches = "", userDestinations = "", userTimeToPost = "" }) {
  const [isOpen, setIsOpen] = useState(false);
  const [showThreads, setShowThreads] = useState(false);
  const [threads, setThreads] = useState([]);
  const [activeThreadId, setActiveThreadId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [inputText, setInputText] = useState("");
  const [selectedImages, setSelectedImages] = useState([]);
  const [isSending, setIsSending] = useState(false);
  const [isLoadingThread, setIsLoadingThread] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [showWebsiteInput, setShowWebsiteInput] = useState(false);
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [attachedFile, setAttachedFile] = useState(null);

  const scrollboxRef = useRef(null);
  const textareaRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const fileInputRef = useRef(null);
  const pendingReelRef = useRef(null); // ← FIX 1

  const assistantScrollToBottom = useCallback(() => {
    if (scrollboxRef.current) scrollboxRef.current.scrollTop = scrollboxRef.current.scrollHeight;
  }, []);

  useEffect(() => { assistantScrollToBottom(); }, [messages, assistantScrollToBottom]);

  useEffect(() => {
    assistantInitialize();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function assistantInitialize() {
    await assistantFetchThreadList();
    const savedThreadId = assistantGetCookie("_assistant_threadid");
    const savedImages = assistantGetCookie("_assistant_images");
    if (savedImages) { try { setSelectedImages(JSON.parse(savedImages)); } catch (_) {} }
    if (savedThreadId) { assistantLoadThread(savedThreadId); } else { assistantCreateThread(); }
  }

  async function assistantFetchThreadList() {
    try {
      const data = await assistantApiFetch(`/threads?user_id=${userId}`);
      setThreads(Array.isArray(data) ? data : []);
    } catch (err) { console.error("assistantFetchThreadList:", err); }
  }

  async function assistantCreateThread() {
    setIsLoadingThread(true); setMessages([]); setShowThreads(false);
    try {
      const data = await assistantApiFetch(`/threads/create?user_id=${userId}`, { method: "POST", body: JSON.stringify({ user_id: userId }) });
      const threadId = data.thread_id;
      assistantPersistThread(threadId);
      await assistantFetchThreadList();
      const userDateTime = assistantGetUserDateTime();
      await assistantSendSystemMessage(threadId,
        `Greet the user ${userName}; they have just started a new conversation. ` +
        `Their preferred niches are ${userNiches}, and their preferred destinations are ${userDestinations}. ` +
        `Be proactive and avoid sounding like you want to end the conversation; the user should feel that you are always there. ` +
        `You are the one who will handle tasks for the user, so communicate in a way that encourages them to ask you to make changes to captions and publish posts, etc. ` +
        `Remember that part of your main objective is to publish content on the user's social media and subtly encourage the user to ask you to do so. ` +
        `Avoid sharing IDs directly with the user. The user's preferred time is ${userTimeToPost}. ` +
        `This chat started on ${userDateTime} in the user's date time.\n\n` + buildCaptionQualityPrompt(),
      );
    } catch (err) { console.error("assistantCreateThread:", err); }
    finally { setIsLoadingThread(false); }
  }

  function buildCaptionQualityPrompt() {
    return `CAPTION QUALITY STANDARD — PERMANENT FIX
When writing or editing hotel and resort captions for travel advisors to post on Instagram, never do the following:

Banned words and phrases (hard stop — never use):
Dream (dream stay, dream escape, dream clients)
Curated, seamless, elevate, wanderlust
Effortlessly / effortless
"Good vibes" or any vibe-language that isn't specific
"Drop a comment or DM me"
"Let's find your [anything]"
"This isn't X, it's Y" constructions
Em dashes
"No pressure" language
Pipeline, unlock

Structural rules:
CTAs must be specific and confident. Never vague. The advisor is the expert — write like one.
Never write FOR travelers. Write AS a travel advisor speaking TO potential clients who need to be convinced to book through them.
Negative constructions ("this isn't a sleepy hideaway") are lazy — replace with a strong positive claim.
Specificity over sensation. Name the bar, the dish, the moment. Don't describe a feeling when you can describe the thing that causes it.
Qualifications (who this property is NOT for) are good — but follow them immediately with a confident close, not a soft ask.

CRITICAL RULE — NO PLACEHOLDERS:
Never output brackets, placeholders, or unfinished variables such as [hotel name], [destination], [insert text], or similar.
All captions must be fully written, complete, and ready to publish.

Voice benchmark: Heather McMahan energy. Confident, specific, a little deadpan, never trying too hard.

Never call the get_user_datetime function; it has been disabled.`;
  }

  async function assistantLoadThread(threadId) {
    setIsLoadingThread(true); setMessages([]); setShowThreads(false);
    assistantPersistThread(threadId);
    try { await assistantFetchMessages(threadId); }
    catch (err) { console.error("assistantLoadThread:", err); }
    finally { setIsLoadingThread(false); }
  }

  function assistantPersistThread(threadId) {
    setActiveThreadId(threadId);
    assistantSetCookie("_assistant_threadid", threadId, 30);
  }

  async function assistantDeleteThread(threadId) {
    setThreads((prev) => prev.filter((t) => t.id !== threadId));
    try {
      await assistantApiFetch(`/threads/${threadId}?user_id=${userId}`, { method: "DELETE", body: JSON.stringify({ user_id: userId }) });
      if (threadId === activeThreadId) { setActiveThreadId(null); setMessages([]); assistantDeleteCookie("_assistant_threadid"); }
    } catch (err) { console.error("assistantDeleteThread:", err); }
  }

  async function assistantUpdateThreadTitle(threadId, title) {
    setThreads((prev) => prev.map((t) => (t.id === threadId ? { ...t, title } : t)));
    try { await assistantApiFetch(`/threads/${threadId}/title`, { method: "PATCH", body: JSON.stringify({ title, user_id: userId }) }); }
    catch (err) { console.error("assistantUpdateThreadTitle:", err); }
  }

  async function assistantFetchMessages(threadId) {
    const data = await assistantApiFetch(`/threads/${threadId}/messages?user_id=${userId}`);
    if (Array.isArray(data)) setMessages(data.map((m) => ({ id: m.id, role: m.role, content: m.content ?? "", timestamp: m.created_at })));
  }

  async function assistantSendSystemMessage(threadId, content) {
    await assistantApiFetch("/send_message", { method: "POST", body: JSON.stringify({ user_id: userId, thread_id: threadId, role: "system", message: content, idimages: null }) });
    await assistantFetchMessages(threadId);
  }

  async function assistantSubmitUserMessage() {
    const content = inputText.trim();
    if (!content || isSending || !activeThreadId) return;
    const idimages = selectedImages.length > 0 ? selectedImages.map((i) => ({ idimage: i.idimage, url: API.baseUrl + `/upload/image/${i.idimage}/image/original.jpg` })) : null;
    setIsSending(true); setInputText(""); setSelectedImages([]);
    assistantDeleteCookie("_assistant_images");
    const tempId = `temp-${Date.now()}`;
    setMessages((prev) => [...prev, { id: tempId, role: "user", content, timestamp: new Date().toISOString() }]);
    try {
      const result = await assistantApiFetch("/send_message", { method: "POST", body: JSON.stringify({ user_id: userId, thread_id: activeThreadId, role: "user", message: content, idimages }) });
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
      const requiredAction = assistantExtractRequiredAction(result);
      if (requiredAction) { await assistantHandleFunctionCall(requiredAction); }
      else { await assistantFetchMessages(activeThreadId); }
    } catch (err) {
      console.error("assistantSubmitUserMessage:", err);
      setMessages((prev) => prev.filter((m) => m.id !== tempId));
    } finally { setIsSending(false); textareaRef.current?.focus(); }
  }

  async function assistantHandleFunctionCall(call) {
    if (!call?.function) return;
    const { name, arguments: args } = call.function;
    let output = `Function '${name}' executed with ${JSON.stringify(args)}`;
    try {
      switch (name) {
        case "suggest_image": output = await assistantFunctionSuggestImage(args.search); output = "Suggest this image to the user (show it in the body): " + output; break;
        case "suggest_reel": output = await assistantFunctionSuggestReel(args.search); output = "Suggest this reel. Display it as a clickable thumbnail in Markdown: [![TEXT](THUMBNAIL)](VIDEO_URL)\n\n" + output; break;
        case "suggest_carousel": output = await assistantFunctionSuggestCarousel(args.search); output = "Suggest this carousel to the user: " + output; break;
        case "favorite_image": output = await assistantFunctionFavoriteImage(args.idimage); break;
        case "save_draft": output = await assistantFunctionSaveDraft(args.idimages, args.content); break;
        case "publish_post": output = await assistantFunctionPublishPost(args.idimages, args.content, args.date, args.time, args.idreel); break;
        case "edit_post": output = await assistantFunctionEditPost(args.iddraft, args.idimages, args.content, args.date, args.time, args.preserveCurrentPostContent); break;
        case "cancel_post": output = await assistantFunctionCancelPost(args.iddraft); break;
        case "list_scheduled_posts": output = await assistantFunctionListScheduledPosts(); break;
        case "list_posted_posts": output = await assistantFunctionListPostedPosts(); break;
        case "get_user_datetime": output = assistantGetUserDateTime(); break;
        default: output = `Unknown function: ${name}`;
      }
    } catch (err) { output = `Error executing ${name}: ${err.message}`; }
    await assistantSendSystemMessage(activeThreadId, `The assistant requested the execution of function '${name}' executed with ${JSON.stringify(args)}. Here is the result: ${output}. Continue the context if necessary.`);
  }

  async function assistantFunctionSuggestImage(search) { const res = await fetch(API.assistant + "?function=suggest_image", { method: "POST", credentials: "include", body: new URLSearchParams({ function: "suggest_image", search }) }); return res.text(); }
  async function assistantFunctionSuggestReel(search) { const res = await fetch(API.assistant + "?function=suggest_reel", { method: "POST", credentials: "include", body: new URLSearchParams({ function: "suggest_reel", search }) }); return res.text(); }
  async function assistantFunctionSuggestCarousel(search) { const res = await fetch(API.assistant + "?function=suggest_carousel", { method: "POST", credentials: "include", body: new URLSearchParams({ function: "suggest_carousel", search }) }); return res.text(); }
  async function assistantFunctionFavoriteImage(idimage) { await fetch(API.images + "favorite=true", { method: "POST", credentials: "include", body: new URLSearchParams({ idimage }) }); return "Image added to favorites"; }

  async function assistantFunctionPublishPost(idimages, content, date, time, idreel) {
    if ((!Array.isArray(idimages) || !idimages.length) && !idreel) return "Post not published: you need to add at least one image or a reel.";
    if (new Date(`${date}T${time}`) < new Date()) return "It is not possible to publish in the past, please select another date.";
    let items = [], type = "image";
    if (idreel) { type = "reel"; items.push({ type: "reel", id: idreel, url: `upload/reel/${idreel}/filewithmusic/file.mp4` }); }
    else { items = idimages.map((id) => ({ type: "image", id, url: `upload/image/${id}/image/original.jpg` })); type = idimages.length === 1 ? "image" : "carousel"; }
    const res = await fetch(API.drafts + "?add=true&returnDetails", { method: "POST", credentials: "include", body: new URLSearchParams({ type, content, facebook: true, instagram: true, linkedin: true, userdatetime: `${date}T${time}`, scheduled: true, posted: true, items: JSON.stringify(items), idreel: idreel || "" }) });
    const result = await res.json();
    return `Post scheduled for ${result.date} at ${result.time}. Post ID: ${result.iddraft}`;
  }

  async function assistantFunctionSaveDraft(idimages, content) {
    if (!Array.isArray(idimages) || !idimages.length) return "Draft not saved: you need to add at least one image.";
    const items = idimages.map((id) => ({ type: "image", id, url: `upload/image/${id}/image/original.jpg` }));
    const res = await fetch(API.drafts + "?add=true&returnDetails", { method: "POST", credentials: "include", body: new URLSearchParams({ type: idimages.length === 1 ? "image" : "carousel", content, facebook: true, instagram: true, linkedin: true, userdatetime: new Date().toISOString().slice(0, 16), scheduled: false, posted: false, items: JSON.stringify(items) }) });
    const result = await res.json();
    return `Draft saved. Post ID: ${result.iddraft}`;
  }

  async function assistantFunctionEditPost(iddraft, idimages, content, date, time, preserveCurrentPostContent) {
    let items = [];
    if (preserveCurrentPostContent) { const res = await fetch(API.drafts + "?load=true", { method: "POST", credentials: "include", body: new URLSearchParams({ iddraft }) }); const draft = await res.json(); items = draft.items || []; }
    else { if (!Array.isArray(idimages) || !idimages.length) return "Post not edited: you need to add at least one image or preserve current post content."; items = idimages.map((id) => ({ type: "image", id, url: `upload/image/${id}/image/original.jpg` })); }
    const res = await fetch(API.drafts + "?edit=true&returnDetails", { method: "POST", credentials: "include", body: new URLSearchParams({ iddraft, type: items.length === 1 ? "image" : "carousel", content, facebook: true, instagram: true, linkedin: true, userdatetime: `${date}T${time}`, scheduled: true, posted: true, items: JSON.stringify(items) }) });
    const result = await res.json();
    return `Post edited. Scheduled for ${result.date} at ${result.time}. Post ID: ${result.iddraft}`;
  }

  async function assistantFunctionCancelPost(iddraft) {
    await fetch(API.drafts + "?unpostDraft=true", { method: "POST", credentials: "include", body: new URLSearchParams({ iddraft }) });
    return `Post canceled. Saved in drafts. Draft ID: ${iddraft}`;
  }

  async function assistantFunctionListScheduledPosts() { return assistantFetchCalendarPosts("scheduled"); }
  async function assistantFunctionListPostedPosts() { return assistantFetchCalendarPosts("posted"); }

  async function assistantFetchCalendarPosts(mode) {
    const now = new Date();
    const month = (now.getMonth() + 1).toString().padStart(2, "0");
    const year = now.getFullYear();
    async function fetchMonth(m, y) {
      const res = await fetch(API.myCalendar + "?list=true", { method: "POST", credentials: "include", body: new URLSearchParams({ month: m, year: y }) });
      const raw = await res.text();
      try { const parsed = JSON.parse(raw); return Array.isArray(parsed) ? parsed : []; } catch { return []; }
    }
    let allPosts = [];
    if (mode === "scheduled") {
      const nextMonth = (((now.getMonth() + 2 - 1) % 12) + 1).toString().padStart(2, "0");
      const nextYear = nextMonth === "01" && month === "12" ? year + 1 : year;
      const [a, b] = await Promise.all([fetchMonth(month, year), fetchMonth(nextMonth, nextYear)]);
      allPosts = [...a, ...b];
    } else {
      const prevMonth = (((now.getMonth() - 1 + 12) % 12) + 1).toString().padStart(2, "0");
      const prevYear = prevMonth === "12" && month === "01" ? year - 1 : year;
      const [a, b] = await Promise.all([fetchMonth(prevMonth, prevYear), fetchMonth(month, year)]);
      allPosts = [...a, ...b];
    }
    const filtered = allPosts.filter((post) => { const dt = new Date(post._userdatetime || `${post.date}T${post.time}`); return mode === "scheduled" ? dt > now : dt <= now; });
    if (!filtered.length) return mode === "scheduled" ? "You don't have any upcoming posts scheduled." : "You don't have any published posts yet.";
    return filtered.map((post, i) => {
      let images = [];
      try { const items = typeof post.items === "string" ? JSON.parse(post.items) : post.items; if (Array.isArray(items)) images = items.filter((item) => item.type === "image" && item.id && item.url).map((item) => ({ idimage: item.id, url: item.url })); } catch (_) {}
      return `Post ${i + 1}:\n- ID Draft: ${post.iddraft}\n- Type: ${post.type}\n- Content: ${post.content}\n- DateTime: ${post._userdatetime || `${post.date}T${post.time}`}\n` + images.map((img, j) => `  * image${j + 1}_idimage: ${img.idimage}\n  * image${j + 1}_url: ${API.baseUrl}/${img.url}`).join("\n");
    }).join("\n\n");
  }

  // ─────────────────────────────────────────
  // IMAGES
  // ─────────────────────────────────────────
  function assistantAddImage(idimage) {
    setSelectedImages((prev) => {
      if (prev.length >= MAX_IMAGES) return prev;
      if (prev.some((img) => img.idimage === idimage)) return prev;
      const url = `${API.baseUrl}/upload/image/${idimage}/image/original.jpg`;
      const updated = [...prev, { idimage, url }].slice(0, MAX_IMAGES);
      assistantSetCookie("_assistant_images", JSON.stringify(updated), 30);
      return updated;
    });
  }

  // ── FIX 2: window exposure with _assistant_reel_add ───────────────────────
  useEffect(() => {
    window._assistant_image_add = (idimage) => {
      setIsOpen(true);
      assistantAddImage(idimage);
    };

    window._assistant_text_add = (text) => {
      setIsOpen(true);
      setInputText(text);
      textareaRef.current?.focus();
    };

    window._assistant_reel_add = (idreel) => {
      const content =
        `[![Reel ${idreel} - Do nothing now, wait for more information from the user.]` +
        `(${API.baseUrl}/upload/reel/${idreel}/image/original.jpg)]` +
        `(${API.baseUrl}/upload/reel/${idreel}/file/file.mp4)`;
      setIsOpen(true);
      pendingReelRef.current = content;
      setInputText(content);
    };

    return () => {
      delete window._assistant_image_add;
      delete window._assistant_text_add;
      delete window._assistant_reel_add;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── FIX 3: auto-submit when inputText matches the pending reel content ─────
  useEffect(() => {
    if (pendingReelRef.current && inputText === pendingReelRef.current) {
      pendingReelRef.current = null;
      assistantSubmitUserMessage();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputText]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = textareaRef.current.scrollHeight + 2 + "px";
    }
  }, [inputText]);

  function assistantRemoveImage(idimage) {
    setSelectedImages((prev) => {
      const updated = prev.filter((img) => img.idimage !== idimage);
      assistantSetCookie("_assistant_images", JSON.stringify(updated), 30);
      return updated;
    });
  }

  // ─────────────────────────────────────────
  // VOICE RECORDING
  // ─────────────────────────────────────────
  async function assistantToggleRecording() {
    if (isRecording) { mediaRecorderRef.current?.stop(); return; }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported("audio/mp4") ? "audio/mp4" : "audio/webm";
      const recorder = new MediaRecorder(stream, { mimeType });
      audioChunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) audioChunksRef.current.push(e.data); };
      recorder.onstop = async () => {
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        const formData = new FormData();
        formData.append("audio", blob, mimeType.includes("mp4") ? "audio.mp4" : "audio.webm");
        try {
          const res = await fetch(API.assistant + "?transcription", { method: "POST", credentials: "include", body: formData });
          const result = await res.json();
          setInputText(result.text || "");
        } catch (err) { console.error("assistantToggleRecording transcription:", err); }
        setIsRecording(false);
      };
      mediaRecorderRef.current = recorder;
      recorder.start();
      setIsRecording(true);
    } catch (err) {
      if (err.name === "NotAllowedError") { alert("Microphone permission denied."); }
      else { alert("Could not access the microphone."); }
    }
  }

  function assistantHandleTextareaChange(e) {
    setInputText(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = e.target.scrollHeight + 2 + "px";
  }

  function assistantHandleKeyDown(e) {
    if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); assistantSubmitUserMessage(); }
  }

  // ─────────────────────────────────────────
  // RENDER
  // ─────────────────────────────────────────

  return (
    <>
      <div className="position-fixed bottom-0 end-0 p-3 mb-5 mb-md-0" style={{ zIndex: 10 }}>
        <button className="btn assistant_trigger-btn btn-lg shadow-lg rounded-5 d-flex align-items-center" type="button" onClick={() => setIsOpen((v) => !v)}>
          <i className="fi fi-rr-bulb me-2" /> Estelle AI
        </button>
      </div>

      {isOpen && (
        <div id="assistant_widget" className="assistant_widget bg-white position-fixed shadow rounded-4">
          <div className="assistant_header rounded-top-3 d-flex justify-content-between align-items-center shadow-sm border-bottom">
            <button className="btn lh-1 d-flex align-items-center" type="button" onClick={() => setShowThreads((v) => !v)}>
              <i className="fi fi-rr-menu-burger fs-5" />
              <span className="ms-3"><strong>Estelle</strong><br /><small className="text-success">● Online</small></span>
            </button>
            <button type="button" className="btn" onClick={() => setIsOpen(false)}><i className="fi fi-rr-cross" /></button>
          </div>

          <div className="assistant_body">
            {showThreads && (
              <AssistantThreadList
                threads={threads}
                onLoad={(id) => { setShowThreads(false); assistantLoadThread(id); }}
                onDelete={assistantDeleteThread}
                onChangeTitle={assistantUpdateThreadTitle}
                onNewChat={() => { setShowThreads(false); assistantCreateThread(); }}
              />
            )}

            {!showThreads && (
              <div className="assistant_chat d-flex flex-column h-100">
                <div ref={scrollboxRef} id="assistant_scrollbox" className="assistant_scrollbox overflow-auto flex-grow-1 p-3">
                  {isLoadingThread ? <AssistantLoadingSpinner /> : (
                    <div id="assistant_messages">
                      {messages.map((msg) => <AssistantMessage key={msg.id} message={msg} />)}
                      {isSending && <AssistantTypingIndicator />}
                    </div>
                  )}
                </div>

                {activeThreadId && (
                  <div className="assistant_input-area p-3 pt-0">
                    <div className="position-relative">
                      <div className="assistant_input-wrapper border rounded-4 p-2 bg-white">
                        <AssistantImagePreview images={selectedImages} onRemove={assistantRemoveImage} />

                        {attachedFile && (
                          <div className="assistant_attached-file border rounded-3 mb-2 small p-2 px-3">
                            <div className="row g-0 align-items-center">
                              <div className="col-8"><i className="fi fi-rr-file-pdf" /> {attachedFile.name}</div>
                              <div className="col-4"><button className="btn small float-end p-0" onClick={() => setAttachedFile(null)}>Remove</button></div>
                            </div>
                          </div>
                        )}

                        {showWebsiteInput && (
                          <input type="text" className="form-control mb-2" placeholder="https://example.com/page" value={websiteUrl} onChange={(e) => setWebsiteUrl(e.target.value)} />
                        )}

                        <textarea
                          ref={textareaRef}
                          id="assistant_input-textarea"
                          className="assistant_textarea form-control bg-white p-0 border-0"
                          rows={1}
                          placeholder="Ask Estelle a question or make a request..."
                          value={inputText}
                          onChange={assistantHandleTextareaChange}
                          onKeyDown={assistantHandleKeyDown}
                          disabled={isSending}
                          autoComplete="off"
                          style={{ resize: "none", overflowY: "hidden", minHeight: "40px" }}
                        />

                        <div className="d-flex justify-content-between align-items-center mt-1">
                          <div className="dropdown">
                            <button className="btn btn-sm btn-light border-0" type="button" data-bs-toggle="dropdown">
                              <i className="fi fi-rr-plus" />
                            </button>
                            <ul className="dropdown-menu">
                              <li>
                                <button className="dropdown-item small" onClick={() => window._upload?.image((id) => window._assistant_image_add?.(id))}>
                                  <i className="fi fi-rr-picture me-2" /> Upload images
                                </button>
                              </li>
                              <li>
                                {/* FIX 4: use new upload system with reel callback */}
                                <button className="dropdown-item small" onClick={() => window._upload?.reel((id) => window._assistant_reel_add?.(id))}>
                                  <i className="fi fi fi-rr-play-alt me-2" /> Upload videos
                                </button>
                              </li>
                              <li>
                                <button className="dropdown-item small" onClick={() => fileInputRef.current?.click()}>
                                  <i className="fi fi-rr-clip me-2" /> Add PDF file
                                </button>
                              </li>
                              <li>
                                <button className="dropdown-item small" onClick={() => setShowWebsiteInput((v) => !v)}>
                                  <i className="fi fi-rr-globe me-2" /> Search on a web page
                                </button>
                              </li>
                            </ul>
                          </div>

                          <div className="d-flex align-items-center gap-2">
                            <button type="button" className={`assistant_record-btn btn btn-sm ${isRecording ? "btn-danger" : "btn-light"}`} onClick={assistantToggleRecording}>
                              {isRecording ? "🔴 Recording…" : "🎙️ Record"}
                            </button>
                            <button type="button" className="assistant_send-btn btn btn-primary border-0 rounded-circle" onClick={assistantSubmitUserMessage} disabled={isSending || !inputText.trim()}>
                              {isSending ? <i className="fi fi-rr-loading" /> : <i className="fi fi-rr-arrow-small-up" />}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    <input ref={fileInputRef} type="file" className="d-none" accept=".pdf" onChange={(e) => setAttachedFile(e.target.files[0] || null)} />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      <style>{`
        #assistant_widget { max-width: 450px; width: 90%; z-index: 1031; bottom: 76px; right: 16px; }
        @media (max-width: 767.98px) {
          #assistant_widget { bottom: 0 !important; right: 0 !important; top: 0 !important; left: 0 !important; width: 100% !important; max-width: 100% !important; border-radius: 0 !important; }
          .assistant_body { height: calc(100vh - 60px) !important; }
        }
        .assistant_body { height: 70vh; overflow-x: hidden; }
        .assistant_scrollbox { color: #000; }
        #assistant_messages { color: #000; }
        .assistant_message { font-size: .85rem; width: 100%; margin-bottom: 4px; }
        .assistant_message p:last-of-type { margin-bottom: 0; }
        .assistant_message li:last-child { margin-bottom: 1rem; }
        .assistant_message h1 { font-size: 1.4rem; font-weight: bolder; }
        .assistant_message h2 { font-size: 1.3rem; font-weight: bolder; }
        .assistant_message h3 { font-size: 1.2rem; font-weight: bolder; }
        .assistant_message h4 { font-size: 1.1rem; font-weight: bolder; }
        .assistant_message img { max-width: 100%; border-radius: 10px; }
        .assistant_message table { margin-top: .5rem; }
        .assistant_bubble { max-width: 80%; padding: 12px; word-break: break-word; }
        .assistant_bubble p { color: #000 !important; }
        .assistant_bubble--assistant { background: #fff !important; border-top-left-radius: 4px; border-top-right-radius: 15px; border-bottom-left-radius: 15px; border-bottom-right-radius: 15px; margin-bottom: 20px; }
        .assistant_bubble--user { color: #000 !important; background: #f4f4f4 !important; border-top-left-radius: 15px; border-top-right-radius: 15px; border-bottom-left-radius: 15px; border-bottom-right-radius: 4px; margin-bottom: 20px; }
        .assistant_bubble--typing { display: flex; align-items: center; gap: 4px; padding: 14px 18px; min-width: 56px; }
        .assistant_typing-dot { width: 8px; height: 8px; background: #aaa; border-radius: 50%; display: inline-block; animation: assistant_bounce 1.2s infinite ease-in-out both; }
        .assistant_typing-dot:nth-child(1) { animation-delay: 0s; }
        .assistant_typing-dot:nth-child(2) { animation-delay: .2s; }
        .assistant_typing-dot:nth-child(3) { animation-delay: .4s; }
        @keyframes assistant_bounce { 0%, 80%, 100% { transform: scale(0); opacity: .5; } 40% { transform: scale(1); opacity: 1; } }
        .assistant_timestamp { margin-top: -12px; margin-bottom: 12px; font-size: 10px; }
        .assistant_avatar { height: 30px; flex-shrink: 0; }
        .assistant_thread-title { color: inherit; font-size: .85rem; }
        .assistant_thread-actions { display: none; }
        .assistant_thread-row:hover .assistant_thread-actions { display: block; }
        .assistant_thread-date { font-size: .75rem; }
        .assistant_loading { position: relative; }
        .assistant_loading-spinner { background: transparent; border: 4px solid #7f56d95c; border-top: 4px solid var(--primary-color, #7f56d9); border-radius: 50%; width: 150px; height: 150px; top: calc(50% - 75px); left: calc(50% - 75px); position: absolute; animation: assistant_spin 1s linear infinite; }
        .assistant_loading-icon { width: 130px; height: 130px; }
        @keyframes assistant_spin { to { transform: rotate(360deg); } }
        .assistant_textarea { outline: none !important; box-shadow: none !important; }
        .assistant_send-btn { padding: 4px !important; font-size: 20px !important; width: 40px; height: 40px; }
        .assistant_trigger-btn { color: #fff; background: linear-gradient(270deg, #7f56d9, #9b7df0, #6a3fd1, #7f56d9); background-size: 500% 500%; animation: assistant_gradient 8s ease infinite; border: none; transition: all .3s ease; position: relative; overflow: hidden; }
        .assistant_trigger-btn::before { content: ""; position: absolute; inset: -3px; border-radius: inherit; background: linear-gradient(270deg, #7f56d9, #9b7df0, #6a3fd1, #7f56d9); background-size: 500% 500%; animation: assistant_gradient 8s ease infinite; filter: blur(12px); opacity: .6; z-index: -1; }
        .assistant_trigger-btn::after { content: ""; position: absolute; top: 0; left: -120%; width: 120%; height: 100%; background: linear-gradient(120deg, transparent, rgba(255,255,255,.35), transparent); animation: assistant_shine 4s linear infinite; }
        .assistant_trigger-btn:hover { transform: translateY(-2px) scale(1.03); box-shadow: 0 12px 35px rgba(127,86,217,.5); color: #fff; }
        @keyframes assistant_gradient { 0% { background-position: 0% 50%; } 50% { background-position: 100% 50%; } 100% { background-position: 0% 50%; } }
        @keyframes assistant_shine { 0% { left: -120%; } 100% { left: 120%; } }
      `}</style>
    </>
  );
}